import {
  getRevenueAnalysisForecastQueryOptions,
  getRevenueAnalysisQueryOptions,
} from '@/entities/facility/api/get-revenue-analysis.query';
import { useFacilityScope } from '@/shared/lib/hooks/use-facility-scope';
import { Loader, Tabs, TooltipElement, Typography } from '@/shared/ui';
import { useQuery } from '@tanstack/react-query';
import { useMemo, useState, type FC } from 'react';
import { seriesPalette } from '../model/series-config';
import {
  REVENUE_METRICS,
  REVENUE_METRIC_LABELS,
  REVENUE_METRIC_TITLES,
  REVENUE_TABS,
  REVENUE_TAB_LABELS,
  isDimensionTab,
  type RevenueAnalysisSeriesView,
  type RevenueMetric,
  type RevenueTab,
} from '../model/types';
import { useMonthWindow } from '../model/use-month-window';
import { ForecastChart } from './forecast-chart';
import { MonthWindowPager } from './month-window-pager';
import { RevenueAnalysisKpiCards } from './revenue-analysis-kpi-cards';
import { RevenueBarChart } from './revenue-bar-chart';
import { RevenueLineChart } from './revenue-line-chart';

const sum = (values: number[]) => values.reduce((a, b) => a + b, 0);

/** Headline card copy, which depends on both tab groups. */
const HEADLINES: Record<RevenueTab, Record<RevenueMetric, string>> = {
  forecasted: {
    revenue: 'Actual Total Revenue',
    revenuePerResident: 'Actual Avg Revenue per Resident',
  },
  payerType: {
    revenue: 'Total Revenue',
    revenuePerResident: 'Avg Revenue per Resident',
  },
  levelsOfCare: {
    revenue: 'Total Revenue',
    revenuePerResident: 'Avg Revenue per Resident',
  },
  medicaidInsurer: {
    revenue: 'Total Medicaid Revenue',
    // Spec names this one for Medicaid specifically, unlike the other tabs.
    revenuePerResident: 'Avg Medicaid Revenue per Resident',
  },
};

/**
 * Headline KPI tooltips, per the tooltip spec (card #189, Anna's comment 331).
 *
 * Keyed by tab *and* metric because the headline itself is — the same card reads
 * "Total Revenue" on Payer Type and "Total Medicaid Revenue" on Medicaid Insurer,
 * and each has its own definition.
 */
const HEADLINE_TOOLTIP: Record<RevenueTab, Record<RevenueMetric, string>> = {
  forecasted: {
    revenue:
      'Total revenue collected during the selected period, excluding forecasted values',
    revenuePerResident:
      'Average revenue generated per resident during the selected period, excluding ' +
      'forecasted values. Calculated as: Actual Total Revenue ÷ Average Number of Residents',
  },
  payerType: {
    revenue:
      'Total payments collected from residents during the selected period',
    revenuePerResident:
      'Average revenue generated per resident during the selected period. ' +
      'Calculated as: Total Revenue ÷ Average Number of Residents',
  },
  levelsOfCare: {
    revenue:
      'Total payments collected from residents during the selected period',
    revenuePerResident:
      'Average revenue generated per resident during the selected period. ' +
      'Calculated as: Total Revenue ÷ Average Number of Residents',
  },
  medicaidInsurer: {
    revenue: 'Total revenue collected from Medicaid during the selected period',
    revenuePerResident:
      'Average revenue generated per Medicaid resident during the selected period. ' +
      'Calculated as: Total Medicaid Revenue ÷ Average Number of Medicaid Residents',
  },
};

/*
 * These tooltips used to append a per-tab data caveat — "Actual months only…",
 * "Excludes Private Pay.", and a note on how Levels of Care attributes revenue.
 * Removed on request (card #189, Anna's comment 346): the tooltips must match
 * the spec verbatim, and the extra sentences made them read as not matching.
 * If any of that context is wanted back, it needs to live somewhere other than
 * these tooltips.
 */

/** Chart-level tooltip; the card title switches with the metric, so this does too. */
const METRIC_TOOLTIP: Record<RevenueMetric, string> = {
  revenue:
    'Displays revenue trends and breakdowns for the selected analysis type over the ' +
    'selected 12-month period',
  revenuePerResident:
    'Displays revenue per resident trends and breakdowns for the selected analysis type ' +
    'over the selected 12-month period',
};

/**
 * "Revenue Analysis" / "Revenue per Resident Analysis" on the Facilities
 * Financial tab (ST #175).
 *
 * Two independent tab groups over one window: the left picks what the months are
 * split by, the right picks which number they are expressed in. The split lives
 * in the request, the metric does not — the response carries revenue *and* the
 * resident count behind it, so the right group is a pure re-render.
 *
 * The window is this card's own and ignores the global dashboard date filter,
 * which is why the component takes no `dateRange` prop.
 */
export const RevenueAnalysisCard: FC = () => {
  const [tab, setTab] = useState<RevenueTab>('forecasted');
  const [metric, setMetric] = useState<RevenueMetric>('revenue');
  const [hoveredKey, setHoveredKey] = useState<string | null>(null);

  const { scopedFacilityId } = useFacilityScope();
  const { endMonth, describe } = useMonthWindow();

  const scope = {
    endMonth: endMonth ?? undefined,
    facilitiesIds: scopedFacilityId ? [scopedFacilityId] : undefined,
  };

  const isForecast = tab === 'forecasted';

  const dimensionQuery = useQuery({
    ...getRevenueAnalysisQueryOptions({
      ...scope,
      dimension: isDimensionTab(tab) ? tab : 'payerType',
    }),
    enabled: !isForecast,
  });

  const forecastQuery = useQuery({
    ...getRevenueAnalysisForecastQueryOptions(scope),
    enabled: isForecast,
  });

  const active = isForecast ? forecastQuery : dimensionQuery;
  const pager = describe(active.data?.window, active.data?.availableRange);

  const dimensionSeries: RevenueAnalysisSeriesView[] = useMemo(() => {
    const data = dimensionQuery.data;
    if (!data || !isDimensionTab(tab)) return [];

    const revenueTotal = sum(
      data.series.flatMap((s) => s.points.map((p) => p.revenueUsd))
    );

    return data.series.map((s, index) => {
      const { color, soft } = seriesPalette(tab, s.key, index);

      if (metric === 'revenue') {
        const total = sum(s.points.map((p) => p.revenueUsd));
        return {
          key: s.key,
          label: s.label,
          color,
          softColor: soft,
          values: s.points.map((p) => p.revenueUsd),
          total,
          sharePct: revenueTotal > 0 ? (total / revenueTotal) * 100 : null,
        };
      }

      // Per-resident is a per-month ratio, so the card is the mean of the months
      // actually drawn — not total revenue over total residents, which would
      // silently weight the busiest months.
      const values = s.points.map((p) =>
        p.residents > 0 ? p.revenueUsd / p.residents : null
      );
      const billed = values.filter((v): v is number => v !== null);

      return {
        key: s.key,
        label: s.label,
        color,
        softColor: soft,
        values,
        total: billed.length > 0 ? sum(billed) / billed.length : 0,
        sharePct: null,
      };
    });
  }, [dimensionQuery.data, metric, tab]);

  const headlineValue = useMemo(() => {
    if (isForecast) {
      const forecastSeries =
        metric === 'revenue'
          ? forecastQuery.data?.revenue
          : forecastQuery.data?.revenuePerResident;
      const actuals = forecastSeries?.actuals ?? [];
      if (!actuals.length) return 0;

      // Both headlines say "Actual": the forecast months are excluded on purpose.
      return metric === 'revenue'
        ? sum(actuals.map((p) => p.value))
        : sum(actuals.map((p) => p.value)) / actuals.length;
    }

    const data = dimensionQuery.data;
    const points = data?.series.flatMap((s) => s.points) ?? [];
    const revenue = sum(points.map((p) => p.revenueUsd));

    if (metric === 'revenue') return revenue;

    // Medicaid Insurer is deliberately excluded: "Total Medicaid Revenue" is already scoped to
    // Medicaid, so its natural denominator is Medicaid residents, not the whole-building count —
    // flagged as a known difference in the tester guide rather than forced to match (Anna, ST #175
    // comment 349 only asks for parity across Forecasted, Payer Type and Levels of Care).
    if (tab === 'medicaidInsurer') {
      const residents = sum(points.map((p) => p.residents));
      return residents > 0 ? revenue / residents : 0;
    }

    // Same construction as the Forecasted tab: the mean of each month's (revenue ÷ that month's
    // building-wide average residents), not total revenue over residents summed across this
    // dimension's own series — which double-counts anyone billed under more than one series in
    // the same month (e.g. a resident with both a Private Pay and a Medicaid line).
    const months = data?.window.months ?? [];
    const avgResidents = data?.monthlyAvgResidents ?? [];
    const revenueByMonth = new Map<string, number>();
    for (const p of points) {
      revenueByMonth.set(
        p.month,
        (revenueByMonth.get(p.month) ?? 0) + p.revenueUsd
      );
    }
    const monthlyRatios = months
      .map((m, i) =>
        avgResidents[i] > 0
          ? (revenueByMonth.get(m) ?? 0) / avgResidents[i]
          : null
      )
      .filter((v): v is number => v !== null);

    return monthlyRatios.length > 0
      ? sum(monthlyRatios) / monthlyRatios.length
      : 0;
  }, [dimensionQuery.data, forecastQuery.data, isForecast, metric, tab]);

  const months = active.data?.window.months ?? [];

  const renderChart = () => {
    if (active.isLoading) {
      return (
        <div className="flex h-full items-center justify-center">
          <Loader />
        </div>
      );
    }

    if (isForecast) {
      const series =
        metric === 'revenue'
          ? forecastQuery.data?.revenue
          : forecastQuery.data?.revenuePerResident;

      if (!series?.actuals.length) return <EmptyState />;

      return <ForecastChart series={series} />;
    }

    if (!dimensionSeries.length) return <EmptyState />;

    return metric === 'revenue' ? (
      <RevenueBarChart
        months={months}
        hoveredKey={hoveredKey}
        series={dimensionSeries}
        onHover={setHoveredKey}
      />
    ) : (
      <RevenueLineChart
        months={months}
        hoveredKey={hoveredKey}
        series={dimensionSeries}
        onHover={setHoveredKey}
      />
    );
  };

  return (
    <div
      // The Financial tab's own strip also renders role="tab" buttons, so the
      // e2e spec needs a handle to scope its queries to this card.
      data-testid="revenue-analysis-card"
      className="border-neutral-5 bg-neutral-1 flex w-full flex-col gap-4 border p-4"
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-x-2">
          <Typography
            as="h5"
            variant="h5"
            weight="medium"
            data-testid="revenue-analysis-title"
          >
            {REVENUE_METRIC_TITLES[metric]}
          </Typography>
          <TooltipElement content={METRIC_TOOLTIP[metric]} />
        </div>

        <MonthWindowPager
          label={pager.label}
          canGoBack={pager.canGoBack}
          canGoForward={pager.canGoForward}
          onBack={pager.goBack}
          onForward={pager.goForward}
        />
      </div>

      <div className="flex flex-wrap items-center justify-between gap-4">
        <Tabs
          // "Levels of Care" is also the name of a Facilities tab, so a by-name
          // lookup finds the wrong control first. Scope to the group by test id.
          data-testid="revenue-analysis-dimension-tabs"
          activeTabId={tab}
          tabs={REVENUE_TABS.map((id) => ({
            id,
            label: REVENUE_TAB_LABELS[id],
          }))}
          onChangeTab={(id) => {
            setTab(id as RevenueTab);
            setHoveredKey(null);
          }}
        />

        <Tabs
          data-testid="revenue-analysis-metric-tabs"
          activeTabId={metric}
          tabs={REVENUE_METRICS.map((id) => ({
            id,
            label: REVENUE_METRIC_LABELS[id],
          }))}
          onChangeTab={(id) => setMetric(id as RevenueMetric)}
        />
      </div>

      <RevenueAnalysisKpiCards
        metric={metric}
        hoveredKey={hoveredKey}
        series={isForecast ? [] : dimensionSeries}
        headline={{
          label: HEADLINES[tab][metric],
          value: headlineValue,
          info: HEADLINE_TOOLTIP[tab][metric],
        }}
        onHover={setHoveredKey}
      />

      <div className="h-[320px]">{renderChart()}</div>
    </div>
  );
};

const EmptyState: FC = () => (
  <div className="flex h-full items-center justify-center">
    <Typography className="text-neutral-8">
      No revenue for the selected period.
    </Typography>
  </div>
);
